import React, { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useQueryClient, useMutation, useInfiniteQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search, Plus, Dumbbell, Play, X, Check, Trophy, Clock, Flame, ChevronRight, Youtube, Star,
  CalendarDays, Coffee, Scale, CalendarPlus, ArrowLeft, Sparkles, ListChecks, Snowflake, HelpCircle, CheckCircle2, XCircle, Loader2, ClipboardList, Pencil, Trash2, Repeat, Zap, Award, TrendingUp, Share2, ArrowLeftRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { SectionHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { txCategory, txEquipment, txMuscle, txMuscles, EQUIPMENT_KEYS } from "@/lib/exerciseLabels";
import { MuscleChips } from "@/components/train/MuscleChips";
import { DifficultyPills, DifficultyText } from "@/components/train/DifficultyPills";
import { AlternativesList } from "@/components/train/AlternativesList";
import { BeginnerProgram } from "@/components/train/BeginnerProgram";
import { localISODate, formatDate } from "@/lib/dates";
import { WORKOUT_SPLITS, SPLIT_KEYS, SPLIT_DAY_KEYS, SPLIT_DAY_LABELS, formatSetRep, getSetRep, bmiToSplit } from "@/lib/workoutSplits";
import { WARMUPS, WARMUP_CATS } from "@/lib/warmups";
import { COOLDOWNS, COOLDOWN_CATS } from "@/lib/cooldowns";
import { cn } from "@/lib/utils";
import confetti from "canvas-confetti";

const CATEGORIES = ["all", "strength", "hypertrophy", "cardio", "hiit", "core", "mobility", "yoga", "olympic"];
const MUSCLE_OPTIONS = [
  "Chest", "Upper Back", "Lats", "Shoulders", "Rear Delts", "Biceps", "Triceps", "Forearms",
  "Core", "Obliques", "Lower Back", "Glutes", "Quadriceps", "Hamstrings", "Calves", "Full Body",
];

const ytSearchUrl = (name, id) =>
  `https://www.youtube.com/results?search_query=${encodeURIComponent((id ? "cara " : "how to ") + name + (id ? " teknik yang benar" : " proper form"))}`;


export default function Train() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [tab, setTab] = useState("start");

  const { data: active } = useQuery({ queryKey: ["active-session"], queryFn: async () => (await api.get("/sessions/active")).data });

  useEffect(() => { if (active) setTab("session"); }, [active]);

  const goSession = () => qc.invalidateQueries({ queryKey: ["active-session"] });

  return (
    <div className="space-y-6">
      <SectionHeader title={t("train")} subtitle={t("exerciseLibrary")} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="start" data-testid="tab-library">{t("startWorkout")}</TabsTrigger>
          <TabsTrigger value="session" data-testid="tab-session">{active ? t("activeSession") : t("activeSession")}</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">{t("history")}</TabsTrigger>
        </TabsList>
        <TabsContent value="start"><StartWorkout onStarted={goSession} /></TabsContent>
        <TabsContent value="session"><Session active={active} onChange={goSession} /></TabsContent>
        <TabsContent value="history"><History onStarted={goSession} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Start: choose Recommended vs Manual ---------------- */
function StartWorkout({ onStarted }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("choose");
  const [pendingStart, setPendingStart] = useState(null);
  const requestStart = (fn) => setPendingStart(() => fn);

  if (mode === "choose") {
    return (
      <div className="slide-up">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight">{t("chooseWorkoutType")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("chooseWorkoutHint")}</p>
          </div>
          <GymTermsGlossary className="shrink-0" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <ModeCard testid="mode-rec" icon={CalendarDays} onClick={() => setMode("rec")}
            title={t("recWorkout")} desc={t("recWorkoutDesc")}
            accent="from-primary/20 to-primary/5" iconClass="bg-primary/15 text-primary" />
          <ModeCard testid="mode-manual" icon={ListChecks} onClick={() => setMode("manual")}
            title={t("manualWorkout")} desc={t("manualWorkoutDesc")}
            accent="from-sky-500/20 to-sky-500/5" iconClass="bg-sky-500/15 text-sky-500" />
          <ModeCard testid="mode-warmup" icon={Flame} onClick={() => setMode("warmup")}
            title={t("warmupMode")} desc={t("warmupModeDesc")}
            accent="from-orange-500/20 to-orange-500/5" iconClass="bg-orange-500/15 text-orange-500" />
          <ModeCard testid="mode-cooldown" icon={Snowflake} onClick={() => setMode("cooldown")}
            title={t("cooldownMode")} desc={t("cooldownModeDesc")}
            accent="from-cyan-500/20 to-cyan-500/5" iconClass="bg-cyan-500/15 text-cyan-500" />
          <ModeCard testid="mode-program" icon={ClipboardList} onClick={() => setMode("program")}
            title={t("programs")} desc={t("programsDesc")}
            accent="from-violet-500/20 to-violet-500/5" iconClass="bg-violet-500/15 text-violet-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="slide-up space-y-4">
      <button onClick={() => setMode("choose")} data-testid="back-to-choose"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> {t("back")}
      </button>
      {mode === "rec" ? <WorkoutPlanner onStarted={onStarted} onRequestStart={requestStart} />
        : mode === "warmup" ? <Warmup />
        : mode === "cooldown" ? <Cooldown />
        : mode === "program" ? <Programs onStarted={onStarted} />
        : <Library onStarted={onStarted} onRequestStart={requestStart} />}

      <Dialog open={!!pendingStart} onOpenChange={(o) => !o && setPendingStart(null)}>
        <DialogContent className="max-w-sm" data-testid="warmup-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-lg"><Flame className="h-5 w-5 text-orange-500" /> {t("warmupPromptTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("warmupPromptDesc")}</p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row-reverse">
            <Button onClick={() => { const fn = pendingStart; setPendingStart(null); fn && fn(); }} className="flex-1" data-testid="warmup-prompt-start">{t("startNow")}</Button>
            <Button variant="outline" onClick={() => { setPendingStart(null); setMode("warmup"); }} className="flex-1 gap-2 border-orange-500/40 text-orange-600 hover:bg-orange-500/10" data-testid="warmup-prompt-warmup"><Flame className="h-4 w-4" /> {t("warmupFirst")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ModeCard({ icon: Icon, title, desc, onClick, accent, iconClass, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="group relative overflow-hidden rounded-3xl border bg-card p-6 text-left transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl">
      <div className={cn("pointer-events-none absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300 group-hover:opacity-100", accent)} />
      <div className="relative">
        <div className={cn("grid h-14 w-14 place-items-center rounded-2xl", iconClass)}><Icon className="h-7 w-7" /></div>
        <h3 className="mt-4 font-display text-xl font-bold tracking-tight">{title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{desc}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
          <ChevronRight className="h-4 w-4" />
        </span>
      </div>
    </button>
  );
}


/* ---------------- Beginner glossary ---------------- */
const GLOSSARY_TERMS = ["termSet", "termRep", "termVolume", "term1rm", "termWarmupSet"];

function GymTermsGlossary({ className }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="glossary-btn"
        className={cn("inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary", className)}>
        <HelpCircle className="h-4 w-4" /> {t("glossaryBtn")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="glossary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-lg"><HelpCircle className="h-5 w-5 text-primary" /> {t("glossaryTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("glossarySubtitle")}</p>
          <div className="mt-1 space-y-3">
            {GLOSSARY_TERMS.map((k) => (
              <div key={k} className="rounded-2xl border bg-accent/40 p-3.5" data-testid={`glossary-${k}`}>
                <div className="font-display font-bold text-primary">{t(k)}</div>
                <p className="mt-0.5 text-sm leading-relaxed text-foreground/90">{t(`${k}Desc`)}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ---------------- Library ---------------- */
function Warmup() {
  const { t, lang } = useI18n();
  const L = lang === "id" ? "id" : "en";
  const [done, setDone] = useState({});
  const doneCount = Object.values(done).filter(Boolean).length;
  const total = WARMUPS.length;
  const prevAll = useRef(false);
  const allDone = doneCount === total;
  useEffect(() => {
    if (allDone && !prevAll.current) {
      confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 }, colors: ["#f97316", "#fb923c", "#fdba74", "#10b981"] });
      toast.success(t("warmupAllDone"));
    }
    prevAll.current = allDone;
  }, [allDone, t]);
  const toggle = (k) => setDone((d) => ({ ...d, [k]: !d[k] }));
  const ytUrl = (w) => `https://www.youtube.com/results?search_query=${encodeURIComponent((lang === "id" ? "cara pemanasan " : "how to warm up ") + w.en + (lang === "id" ? " yang benar" : " proper form"))}`;

  return (
    <div className="space-y-5" data-testid="warmup-view">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-orange-500/15 text-orange-500"><Flame className="h-6 w-6" /></span>
        <div>
          <h3 className="font-display text-lg font-bold tracking-tight">{t("warmupTitle")}</h3>
          <p className="text-sm text-muted-foreground">{t("warmupIntro")}</p>
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-4" data-testid="warmup-progress">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-display font-semibold">{t("warmupTitle")}</span>
          <span className="tabular-nums font-semibold text-orange-500">{doneCount}/{total} {t("warmupProgress")}</span>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full bg-muted">
          <div className={cn("h-full rounded-full transition-all duration-500", allDone ? "bg-emerald-500" : "bg-orange-500")} style={{ width: `${Math.round((doneCount / total) * 100)}%` }} />
        </div>
      </div>

      <div className="space-y-2.5">
        {WARMUPS.map((w, i) => {
          const isDone = !!done[w.key];
          return (
            <div key={w.key} className={cn("flex items-center gap-3 rounded-2xl border bg-card p-3.5 transition-colors", isDone && "border-emerald-500/40 bg-emerald-500/5")} data-testid={`warmup-${w.key}`}>
              <button onClick={() => toggle(w.key)} data-testid={`warmup-check-${w.key}`}
                className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-xl transition-colors", isDone ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground hover:text-orange-500")}>
                {isDone ? <Check className="h-4.5 w-4.5" /> : <span className="text-xs font-bold tabular-nums">{i + 1}</span>}
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn("font-display font-semibold", isDone && "text-emerald-600 line-through decoration-emerald-500/50")}>{w[L]}</span>
                  <span className="rounded-md bg-orange-500/10 px-2 py-0.5 text-[11px] font-semibold text-orange-500">{L === "id" ? w.dur : w.durEn}</span>
                  <span className="rounded-md bg-accent px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{WARMUP_CATS[w.cat][L]}</span>
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{L === "id" ? w.hint : w.hintEn}</p>
              </div>
              <a href={ytUrl(w)} target="_blank" rel="noopener noreferrer" data-testid={`warmup-video-${w.key}`} aria-label={t("videoGuide")}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-red-500 transition-colors hover:bg-red-500/10">
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Cooldown({ onEnd, embedded }) {
  const { t, lang } = useI18n();
  const L = lang === "id" ? "id" : "en";
  const [done, setDone] = useState({});
  const doneCount = Object.values(done).filter(Boolean).length;
  const total = COOLDOWNS.length;
  const prevAll = useRef(false);
  const allDone = doneCount === total;
  useEffect(() => {
    if (allDone && !prevAll.current) {
      confetti({ particleCount: 90, spread: 70, origin: { y: 0.6 }, colors: ["#06b6d4", "#22d3ee", "#67e8f9", "#10b981"] });
      toast.success(t("cooldownAllDone"));
    }
    prevAll.current = allDone;
  }, [allDone, t]);
  const toggle = (k) => setDone((d) => ({ ...d, [k]: !d[k] }));
  const ytUrl = (w) => `https://www.youtube.com/results?search_query=${encodeURIComponent((lang === "id" ? "cara peregangan " : "how to stretch ") + w.en + (lang === "id" ? " yang benar" : " proper form"))}`;

  return (
    <div className="space-y-5" data-testid="cooldown-view">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-cyan-500/15 text-cyan-500"><Snowflake className="h-6 w-6" /></span>
        <div>
          <h3 className="font-display text-lg font-bold tracking-tight">{t("cooldownTitle")}</h3>
          <p className="text-sm text-muted-foreground">{t("cooldownIntro")}</p>
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-4" data-testid="cooldown-progress">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="font-display font-semibold">{t("cooldownTitle")}</span>
          <span className="tabular-nums font-semibold text-cyan-500">{doneCount}/{total} {t("cooldownProgress")}</span>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full bg-muted">
          <div className={cn("h-full rounded-full transition-all duration-500", allDone ? "bg-emerald-500" : "bg-cyan-500")} style={{ width: `${Math.round((doneCount / total) * 100)}%` }} />
        </div>
      </div>

      <div className="space-y-2.5">
        {COOLDOWNS.map((w, i) => {
          const isDone = !!done[w.key];
          return (
            <div key={w.key} className={cn("flex items-center gap-3 rounded-2xl border bg-card p-3.5 transition-colors", isDone && "border-emerald-500/40 bg-emerald-500/5")} data-testid={`cooldown-${w.key}`}>
              <button onClick={() => toggle(w.key)} data-testid={`cooldown-check-${w.key}`}
                className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-xl transition-colors", isDone ? "bg-emerald-500 text-white" : "bg-muted text-muted-foreground hover:text-cyan-500")}>
                {isDone ? <Check className="h-4.5 w-4.5" /> : <span className="text-xs font-bold tabular-nums">{i + 1}</span>}
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn("font-display font-semibold", isDone && "text-emerald-600 line-through decoration-emerald-500/50")}>{w[L]}</span>
                  <span className="rounded-md bg-cyan-500/10 px-2 py-0.5 text-[11px] font-semibold text-cyan-500">{L === "id" ? w.dur : w.durEn}</span>
                  <span className="rounded-md bg-accent px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{COOLDOWN_CATS[w.cat][L]}</span>
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{L === "id" ? w.hint : w.hintEn}</p>
              </div>
              <a href={ytUrl(w)} target="_blank" rel="noopener noreferrer" data-testid={`cooldown-video-${w.key}`} aria-label={t("videoGuide")}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-red-500 transition-colors hover:bg-red-500/10">
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          );
        })}
      </div>

      {embedded && (
        <Button onClick={onEnd} size="lg" className="w-full gap-2" data-testid="cooldown-finish-btn">
          <Check className="h-4 w-4" /> {t("finishNow")}
        </Button>
      )}
    </div>
  );
}

function Programs({ onStarted }) {
  const { t, lang } = useI18n();
  const L = lang === "id" ? "id" : "en";
  const qc = useQueryClient();
  const [builder, setBuilder] = useState(null); // null | {} for new | program object for edit
  const [startingId, setStartingId] = useState(null);

  const { data: programs, isLoading } = useQuery({ queryKey: ["programs"], queryFn: async () => (await api.get("/programs")).data });

  const del = async (pid) => {
    await api.delete(`/programs/${pid}`);
    qc.invalidateQueries({ queryKey: ["programs"] });
    toast.success(t("deleted"));
  };

  const startProgram = async (p) => {
    setStartingId(p.id);
    try {
      const logs = (p.exercises || []).map((e) => ({
        exercise_id: e.exercise_id,
        exercise_name: lang === "id" ? (e.name_id || e.exercise_name) : e.exercise_name,
        type: e.type || "weight_reps",
        sets: Array.from({ length: e.sets || 3 }, () => ({ weight: "", reps: "", warmup: false })),
      }));
      if (!logs.length) { toast.error(t("noExercisesYet")); return; }
      const { data: session } = await api.post("/sessions", { name: p.name });
      await api.put(`/sessions/${session.id}`, { logs });
      await qc.invalidateQueries({ queryKey: ["active-session"] });
      onStarted?.();
      toast.success(t("recSessionStarted"));
    } catch { toast.error("Error"); } finally { setStartingId(null); }
  };

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5" data-testid="programs-view">
      <BeginnerProgram onStarted={onStarted} />
      <div className="flex flex-col gap-3 rounded-3xl border bg-gradient-to-br from-violet-500/10 to-transparent p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-violet-500/15 text-violet-500"><ClipboardList className="h-6 w-6" /></span>
          <div>
            <h3 className="font-display text-lg font-bold tracking-tight">{t("programs")}</h3>
            <p className="text-sm text-muted-foreground">{t("programsDesc")}</p>
          </div>
        </div>
        <Button onClick={() => setBuilder({})} size="lg" className="gap-2" data-testid="new-program-btn"><Plus className="h-4 w-4" /> {t("newProgram")}</Button>
      </div>

      {!programs?.length ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border bg-card py-14 text-center">
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-violet-500/10 text-violet-500"><ClipboardList className="h-7 w-7" /></span>
          <p className="max-w-xs text-sm text-muted-foreground">{t("noPrograms")}</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {programs.map((p) => (
            <div key={p.id} className="rounded-2xl border bg-card p-4" data-testid={`program-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{(p.exercises || []).length} {t("exercisesLower")}</div>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => setBuilder(p)} className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground" data-testid={`edit-program-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(p.id)} className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:text-destructive" data-testid={`delete-program-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(p.exercises || []).slice(0, 4).map((e, i) => (
                  <span key={i} className="rounded-md bg-accent px-2 py-0.5 text-[11px] text-muted-foreground">{lang === "id" ? (e.name_id || e.exercise_name) : e.exercise_name}</span>
                ))}
                {(p.exercises || []).length > 4 && <span className="rounded-md bg-accent px-2 py-0.5 text-[11px] text-muted-foreground">+{p.exercises.length - 4}</span>}
              </div>
              <Button onClick={() => startProgram(p)} disabled={startingId === p.id} className="mt-3 w-full gap-2" data-testid={`start-program-${p.id}`}>
                {startingId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {t("startProgram")}
              </Button>
            </div>
          ))}
        </div>
      )}

      {builder !== null && <ProgramBuilder program={builder} onClose={() => setBuilder(null)} />}
    </div>
  );
}

function ProgramBuilder({ program, onClose }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const isEdit = !!program?.id;
  const [name, setName] = useState(program?.name || "");
  const [exercises, setExercises] = useState(program?.exercises || []);
  const [picker, setPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const addExercise = (ex) => {
    setPicker(false);
    setExercises((prev) => [...prev, {
      exercise_id: ex.id,
      exercise_name: ex.name,
      name_id: ex.name_id,
      type: ex.type || "weight_reps",
      sets: 3,
    }]);
  };
  const setSets = (i, v) => setExercises((prev) => prev.map((e, idx) => idx === i ? { ...e, sets: Math.max(1, Math.min(10, v || 1)) } : e));
  const remove = (i) => setExercises((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) return toast.error(t("programName"));
    if (!exercises.length) return toast.error(t("noExercisesYet"));
    setSaving(true);
    try {
      const body = { name: name.trim(), exercises };
      if (isEdit) await api.put(`/programs/${program.id}`, body);
      else await api.post("/programs", body);
      qc.invalidateQueries({ queryKey: ["programs"] });
      toast.success(t("saved"));
      onClose();
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="program-builder">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-lg"><ClipboardList className="h-5 w-5 text-violet-500" /> {isEdit ? t("editProgram") : t("newProgram")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>{t("programName")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("programNamePlaceholder")} className="mt-1.5" data-testid="program-name-input" autoFocus />
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label>{t("exercisesLabel")}</Label>
              <span className="text-xs text-muted-foreground tabular-nums">{exercises.length}</span>
            </div>
            <div className="max-h-56 space-y-2 overflow-y-auto no-scrollbar">
              {exercises.map((e, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg border bg-card p-2" data-testid={`builder-ex-${i}`}>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{lang === "id" ? (e.name_id || e.exercise_name) : e.exercise_name}</span>
                  <div className="flex shrink-0 items-center gap-1">
                    <Input type="number" min="1" max="10" value={e.sets} onChange={(ev) => setSets(i, parseInt(ev.target.value, 10))} className="h-8 w-14 px-1 text-center tabular-nums" data-testid={`builder-sets-${i}`} />
                    <span className="text-xs text-muted-foreground">{t("set").toLowerCase()}</span>
                    <button onClick={() => remove(i)} className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              {!exercises.length && <p className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">{t("noExercisesYet")}</p>}
            </div>
            <Button variant="outline" onClick={() => setPicker(true)} className="mt-2 w-full gap-2" data-testid="builder-add-exercise"><Plus className="h-4 w-4" /> {t("addExercise")}</Button>
          </div>
          <Button onClick={save} disabled={saving} className="w-full gap-2" data-testid="save-program-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t("saveProgram")}
          </Button>
        </div>
      </DialogContent>
      <ExercisePicker open={picker} onClose={() => setPicker(false)} onPick={addExercise} />
    </Dialog>
  );
}

function WorkoutPlanner({ onStarted, onRequestStart }) {
  const { t, lang } = useI18n();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const L = lang === "id" ? "id" : "en";
  const todayKey = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][new Date().getDay()];
  const { data: dash } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });
  const bmi = dash?.bmi;
  const bmiCat = dash?.bmi_category;
  const recommended = bmiCat ? bmiToSplit(bmiCat) : null;
  const GOAL_SPLIT = { mass: "brosplit", strength: "upperlower", fatloss: "fatloss" };
  const [goal, setGoal] = useState(() => user?.profile?.training_goal || "auto");
  const [split, setSplit] = useState("beginner");
  const [manual, setManual] = useState(false);
  const [day, setDay] = useState(todayKey);
  const [applying, setApplying] = useState(false);
  const [starting, setStarting] = useState(false);
  const autoSplit = goal !== "auto" ? GOAL_SPLIT[goal] : recommended;
  useEffect(() => {
    if (autoSplit && !manual) setSplit(autoSplit);
  }, [autoSplit, manual]);
  const pickSplit = (s) => { setManual(true); setSplit(s); };
  const pickGoal = async (g) => {
    setManual(false);
    setGoal(g);
    const ns = g !== "auto" ? GOAL_SPLIT[g] : recommended;
    if (ns) setSplit(ns);
    try {
      const { data } = await api.put("/profile", { profile: { training_goal: g } });
      setUser(data);
    } catch { /* keep local selection */ }
  };
  const reason = (() => {
    const key = { underweight: "recReasonUnderweight", normal: "recReasonNormal", overweight: "recReasonOverweight", obese: "recReasonObese" }[bmiCat];
    return key ? t(key).replace("{bmi}", bmi) : null;
  })();
  const GOALS = [
    { key: "mass", label: t("goalMass") },
    { key: "strength", label: t("goalStrength") },
    { key: "fatloss", label: t("goalFatloss") },
    { key: "auto", label: t("goalAuto") },
  ];
  const plan = WORKOUT_SPLITS[split];
  const d = plan.days[day];
  const typeIcon = d.type === "rest" ? Coffee : d.type === "bodycomp" ? Scale : Dumbbell;
  const TypeIcon = typeIcon;

  const { data: libEx } = useQuery({ queryKey: ["exercises", "core"], queryFn: async () => (await api.get("/exercises", { params: { core: true } })).data });
  const nameMap = useMemo(() => {
    const m = {};
    (libEx || []).forEach((e) => { m[e.name.toLowerCase()] = e.name_id || e.name; });
    return m;
  }, [libEx]);
  const dispName = (en) => (lang === "id" ? (nameMap[en.toLowerCase()] || en) : en);

  const applyToCalendar = async () => {
    setApplying(true);
    try {
      const now = new Date();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      const weekStart = localISODate(monday);
      await api.post("/schedule/apply-template", { week_start: weekStart, template: plan.templateKey });
      toast.success(t("planAppliedCalendar"));
      navigate("/calendar");
    } catch { toast.error("Error"); } finally { setApplying(false); }
  };

  const startFromRec = async () => {
    setStarting(true);
    try {
      const lib = (await api.get("/exercises", { params: { core: true } })).data || [];
      const byName = {};
      lib.forEach((ex) => { byName[ex.name.toLowerCase()] = ex; });
      const logs = [];
      d.exercises.forEach((name) => {
        const ex = byName[name.toLowerCase()];
        if (!ex) return;
        const { sets } = getSetRep(name);
        logs.push({
          exercise_id: ex.id,
          exercise_name: lang === "id" ? ex.name_id : ex.name,
          type: ex.type,
          sets: Array.from({ length: sets }, () => ({ weight: "", reps: "", warmup: false })),
        });
      });
      if (!logs.length) { toast.error(t("recNoMatch")); return; }
      const { data: session } = await api.post("/sessions", { name: `${plan.label[L]} · ${d[L]}` });
      await api.put(`/sessions/${session.id}`, { logs });
      await qc.invalidateQueries({ queryKey: ["active-session"] });
      onStarted?.();
      toast.success(t("recSessionStarted"));
    } catch { toast.error("Error"); } finally { setStarting(false); }
  };

  return (
    <div className="space-y-5" data-testid="workout-planner">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/15 text-primary"><CalendarDays className="h-6 w-6" /></span>
        <div>
          <h3 className="font-display text-lg font-bold tracking-tight">{t("workoutPlanTitle")}</h3>
          <p className="text-sm text-muted-foreground">{t("workoutPlanSubtitle")}</p>
        </div>
      </div>

      <div>
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("yourGoal")}</Label>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="goal-toggle">
          {GOALS.map((g) => (
            <button key={g.key} onClick={() => pickGoal(g.key)} data-testid={`goal-${g.key}`}
              className={cn("rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200",
                goal === g.key ? "border-primary bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-accent")}>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("chooseSplit")}</Label>
        {goal === "auto" ? (
          reason ? (
            <div className="mt-2 flex items-start gap-3 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-4" data-testid="bmi-rec-banner">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary"><Sparkles className="h-5 w-5" /></span>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-primary">{t("recommendedForYou")}</span>
                  <Badge variant="secondary" className="tabular-nums">BMI {bmi}</Badge>
                </div>
                <p className="mt-1 text-sm leading-snug text-foreground">{reason}</p>
              </div>
            </div>
          ) : (
            <p className="mt-2 rounded-2xl border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground" data-testid="bmi-rec-empty">{t("recNoBmi")}</p>
          )
        ) : (
          <div className="mt-2 flex items-start gap-3 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-4" data-testid="goal-note-banner">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary"><Trophy className="h-5 w-5" /></span>
            <p className="min-w-0 text-sm leading-snug text-foreground">{t("goalNote")}</p>
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2" data-testid="split-toggle">
          {SPLIT_KEYS.map((s) => (
            <button key={s} onClick={() => pickSplit(s)} data-testid={`split-${s}`}
              className={cn("relative rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200",
                split === s ? "border-primary bg-primary/10 text-primary shadow-sm" : "text-muted-foreground hover:bg-accent")}>
              {WORKOUT_SPLITS[s].label[L]}
              {goal === "auto" && s === recommended && (
                <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase text-primary-foreground align-middle" data-testid={`split-rec-badge-${s}`}>
                  <CheckCircle2 className="h-2.5 w-2.5" /> {t("recBadge")}
                </span>
              )}
            </button>
          ))}
        </div>
        <p className="mt-2.5 text-sm text-muted-foreground">{plan.desc[L]}</p>
      </div>

      <div>
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("chooseDay")}</Label>
        <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="split-day-tabs">
          {SPLIT_DAY_KEYS.map((dk) => {
            const dd = plan.days[dk];
            const isRest = dd.type === "rest";
            const activeDay = day === dk;
            return (
              <button key={dk} onClick={() => setDay(dk)} data-testid={`split-day-${dk}`}
                className={cn("flex min-w-[76px] shrink-0 flex-col items-center gap-0.5 rounded-2xl border px-3 py-2.5 text-sm font-semibold transition-all duration-200",
                  activeDay ? "border-primary bg-primary text-primary-foreground shadow-md" : "border-transparent bg-accent text-muted-foreground hover:text-foreground")}>
                <span className="flex items-center gap-1">{SPLIT_DAY_LABELS[L][dk]}{dk === todayKey && <span className={cn("h-1.5 w-1.5 rounded-full", activeDay ? "bg-primary-foreground" : "bg-primary")} />}</span>
                <span className={cn("max-w-[68px] truncate text-[11px] font-normal", (activeDay ? "text-primary-foreground/80" : "text-muted-foreground/70"), isRest && "opacity-70")}>{dd[L]}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="rounded-3xl border bg-card p-5" data-testid="split-day-detail">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-accent"><TypeIcon className="h-4.5 w-4.5 text-primary" /></span>
          <div>
            <div className="font-display text-base font-bold">{SPLIT_DAY_LABELS[L][day]}</div>
            <div className="text-xs text-muted-foreground">{d[L]}</div>
          </div>
        </div>
        {d.exercises.length > 0 ? (
          <ul className="mt-4 space-y-2">
            {d.exercises.map((ex, i) => (
              <li key={i} className="flex items-center gap-3 rounded-2xl border bg-background px-3 py-2.5" data-testid={`rec-exercise-${i}`}>
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary/10 text-xs font-bold text-primary tabular-nums">{i + 1}</span>
                <span className="flex-1 truncate text-sm font-medium">{dispName(ex)}</span>
                <span className="hidden shrink-0 rounded-lg bg-accent px-2.5 py-1 text-[11px] font-semibold tabular-nums text-muted-foreground sm:inline" data-testid={`rec-setrep-${i}`}>{formatSetRep(ex)}</span>
                <a href={ytSearchUrl(dispName(ex), lang === "id")} target="_blank" rel="noopener noreferrer" data-testid={`rec-video-${i}`} aria-label={t("videoGuide")}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-red-500 transition-colors hover:bg-red-500/10">
                  <Youtube className="h-4.5 w-4.5" />
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">{d.type === "rest" ? t("restDayHint") : t("bodycompHint")}</p>
        )}
      </div>

      {d.exercises.length > 0 && (
        <Button onClick={() => onRequestStart(startFromRec)} disabled={starting} size="lg" className="w-full gap-2" data-testid="start-from-rec-btn">
          {starting ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Play className="h-4 w-4" />} {t("startFromRec")}
        </Button>
      )}
      <Button onClick={applyToCalendar} disabled={applying} variant="outline" className="w-full gap-2" data-testid="apply-split-btn">
        {applying ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <CalendarPlus className="h-4 w-4" />} {t("applyToCalendar")}
      </Button>
    </div>
  );
}

const LIB_PAGE = 30;

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

/* ---------------- Manual: exercise library ---------------- */
function Library({ onStarted, onRequestStart }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [cat, setCat] = useState("all");
  const [muscle, setMuscle] = useState("all");
  const [equip, setEquip] = useState("all");
  const { user } = useAuth();
  const isBeginner = user?.profile?.experience === "beginner";
  const [level, setLevel] = useState(isBeginner ? "beginner" : "all");
  const [selected, setSelected] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [favOnly, setFavOnly] = useState(false);
  const [startingManual, setStartingManual] = useState(false);

  const startManual = async () => {
    setStartingManual(true);
    try {
      await api.post("/sessions", { name: t("freeWorkout") });
      await qc.invalidateQueries({ queryKey: ["active-session"] });
      onStarted?.();
      toast.success(t("recSessionStarted"));
    } catch { toast.error("Error"); } finally { setStartingManual(false); }
  };

  const { data: pages, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["exercises", "lib", cat, dq, muscle, equip, level, favOnly],
    queryFn: async ({ pageParam }) => (await api.get("/exercises", {
      params: { category: cat, q: dq, muscle, equipment: equip, difficulty: level, favorites: favOnly, limit: LIB_PAGE, offset: pageParam },
    })).data,
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.length === LIB_PAGE ? all.length * LIB_PAGE : undefined),
  });
  const { data: stats } = useQuery({ queryKey: ["exercises", "stats"], queryFn: async () => (await api.get("/exercises/stats")).data });
  const { data: favorites } = useQuery({ queryKey: ["favorites"], queryFn: async () => (await api.get("/favorites")).data });
  const favSet = new Set(favorites || []);

  const toggleFav = useMutation({
    mutationFn: async (ex) => {
      if (favSet.has(ex.id)) await api.delete(`/favorites/${ex.id}`);
      else await api.post("/favorites", { exercise_id: ex.id });
    },
    onSuccess: (_d, ex) => {
      toast.success(favSet.has(ex.id) ? t("removedFromFav") : t("addedToFav"));
      qc.invalidateQueries({ queryKey: ["favorites"] });
      if (favOnly) qc.invalidateQueries({ queryKey: ["exercises", "lib"] });
    },
  });

  const shown = (pages?.pages || []).flat();

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-3xl border bg-gradient-to-br from-sky-500/10 to-transparent p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-sky-500/15 text-sky-500"><ListChecks className="h-6 w-6" /></span>
          <div>
            <h3 className="font-display text-lg font-bold tracking-tight">{t("manualWorkout")}</h3>
            <p className="text-sm text-muted-foreground">{t("browseExercises")}</p>
          </div>
        </div>
        <Button onClick={() => onRequestStart(startManual)} disabled={startingManual} size="lg" className="gap-2" data-testid="start-manual-btn">
          {startingManual ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Play className="h-4 w-4" />} {t("startManualWorkout")}
        </Button>
      </div>

      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "id" ? `Cari ${stats?.total ? stats.total.toLocaleString("id-ID") + " " : ""}gerakan...` : `Search ${stats?.total ? stats.total.toLocaleString("en-US") + " " : ""}exercises...`} className="pl-9" data-testid="exercise-search" />
        </div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="w-full" data-testid="exercise-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c === "all" ? (lang === "id" ? "Semua kategori" : "All categories") : txCategory(c, lang)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={equip} onValueChange={setEquip}>
            <SelectTrigger className="w-full" data-testid="exercise-equipment"><SelectValue /></SelectTrigger>
            <SelectContent>{EQUIPMENT_KEYS.map((k) => <SelectItem key={k} value={k}>{k === "all" ? (lang === "id" ? "Semua alat" : "All equipment") : txEquipment(k, lang)}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant={favOnly ? "default" : "outline"} onClick={() => setFavOnly((v) => !v)} className="gap-2" data-testid="fav-filter-btn">
            <Star className={cn("h-4 w-4", favOnly && "fill-current")} /> {t("favorites")}
          </Button>
          <Button variant="outline" onClick={() => setShowCustom(true)} className="gap-2" data-testid="add-custom-exercise"><Plus className="h-4 w-4" /> {t("customExercise")}</Button>
        </div>
      </div>

      <DifficultyPills value={level} onChange={setLevel} stats={stats} lang={lang} />
      {isBeginner && level === "beginner" && (
        <p className="-mt-1 text-xs text-muted-foreground" data-testid="beginner-auto-hint">
          {lang === "id" ? "Menampilkan gerakan Pemula sesuai level di profilmu." : "Showing Beginner moves based on your profile level."}
        </p>
      )}
      <MuscleChips value={muscle} onChange={setMuscle} stats={stats} lang={lang} />

      {isLoading ? (
        <div className="grid gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl sm:h-28" />)}</div>
      ) : shown.length ? (
        <div className="grid gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3" data-testid="exercise-list">
          {shown.map((ex) => {
            const fav = favSet.has(ex.id);
            return (
              <div key={ex.id} className="group relative min-h-[5.5rem] rounded-2xl border bg-card p-3 pr-14 transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40 sm:min-h-0 sm:p-4 sm:pb-12 sm:pr-4" data-testid={`exercise-card-${ex.id}`}>
                <div className="pointer-events-none absolute inset-y-2 right-2 z-10 flex flex-col items-end justify-between sm:inset-y-3 sm:right-3">
                  <button onClick={(e) => { e.stopPropagation(); toggleFav.mutate(ex); }} data-testid={`fav-${ex.id}`}
                    className={cn("pointer-events-auto grid h-9 w-9 place-items-center rounded-lg transition-colors sm:h-8 sm:w-8",
                      fav ? "text-amber-400" : "text-muted-foreground hover:text-amber-400")}
                    aria-label={t("favorites")}>
                    <Star className={cn("h-4 w-4", fav && "fill-current")} />
                  </button>
                  <a href={ytSearchUrl(lang === "id" ? (ex.name_id || ex.name) : ex.name, lang === "id")} target="_blank" rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()} data-testid={`card-video-${ex.id}`} aria-label={t("videoGuide")}
                    className="pointer-events-auto grid h-9 w-9 place-items-center rounded-lg bg-red-500/10 text-[11px] font-semibold text-red-500 transition-colors hover:bg-red-500/20 sm:inline-flex sm:h-auto sm:w-auto sm:items-center sm:gap-1 sm:px-2 sm:py-1">
                    <Youtube className="h-4 w-4 sm:h-3.5 sm:w-3.5" /> <span className="hidden sm:inline">{t("videoGuide")}</span>
                  </a>
                </div>
                <button onClick={() => setSelected(ex)} data-testid={`exercise-${ex.id}`} className="flex w-full items-center gap-3 text-left sm:block">
                  <div className="flex shrink-0 items-start justify-between sm:pr-8">
                    <ExerciseGif url={ex.gif_url} name={lang === "id" ? ex.name_id : ex.name} rounded="rounded-xl" className="h-14 w-14 shrink-0"
                      fallback={<div className="grid h-14 w-14 place-items-center rounded-xl bg-accent text-accent-foreground sm:h-10 sm:w-10"><Dumbbell className="h-5 w-5" /></div>} />
                    <Badge variant="secondary" className="hidden capitalize sm:inline-flex">{txCategory(ex.category, lang)}</Badge>
                  </div>
                  <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 font-display text-[15px] font-semibold leading-snug sm:mt-3 sm:text-base">{lang === "id" ? ex.name_id : ex.name}</h3>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground sm:mt-1">{txMuscles(ex.primary_muscles, lang)}</p>
                  <div className="mt-1 flex items-center gap-2 truncate text-[11px] text-muted-foreground sm:mt-2">
                    <span className="truncate capitalize">{txEquipment(ex.equipment, lang)}</span> · <DifficultyText level={ex.difficulty} lang={lang} />
                  </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState icon={favOnly ? Star : Search} title={favOnly ? t("noFavorites") : t("none")} description={favOnly ? t("noFavoritesHint") : t("startTracking")} />
      )}

      {hasNextPage && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" size="lg" onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="gap-2" data-testid="load-more-exercises">
            {isFetchingNextPage && <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />}
            {lang === "id" ? "Muat lebih banyak" : "Load more"}
          </Button>
        </div>
      )}

      <ExerciseDetail ex={selected} onClose={() => setSelected(null)} isFav={selected && favSet.has(selected.id)} onToggleFav={() => selected && toggleFav.mutate(selected)}
        onOpenExercise={async (a) => { try { setSelected((await api.get(`/exercises/${a.id}`)).data); } catch { toast.error("Error"); } }} />
      <CustomExerciseDialog open={showCustom} onClose={() => setShowCustom(false)} onSaved={() => qc.invalidateQueries({ queryKey: ["exercises"] })} />
    </div>
  );
}

function ExerciseGif({ url, name, className, rounded = "rounded-2xl", label, fallback = null }) {
  const [loaded, setLoaded] = useState(false);
  const [err, setErr] = useState(false);
  if (!url || err) return fallback;
  const img = (
    <div className={cn("relative overflow-hidden border bg-white", rounded, className)} data-testid="exercise-gif">
      {!loaded && <div className="absolute inset-0 animate-pulse bg-muted" />}
      <img src={url} alt={name} loading="lazy" onLoad={() => setLoaded(true)} onError={() => setErr(true)}
        className={cn("h-full w-full object-contain transition-opacity duration-300", loaded ? "opacity-100" : "opacity-0")} />
    </div>
  );
  return label ? <div><SectionLabel>{label}</SectionLabel>{img}</div> : img;
}

function ExerciseDetail({ ex, onClose, isFav, onToggleFav, onOpenExercise }) {
  const { t, lang } = useI18n();
  const id = lang === "id";
  if (!ex) return null;
  const instructions = id ? (ex.instructions_id || ex.instructions) : ex.instructions;
  const formCues = id ? (ex.form_cues_id?.length ? ex.form_cues_id : ex.form_cues) : ex.form_cues;
  const commonMistakes = id ? (ex.common_mistakes_id?.length ? ex.common_mistakes_id : ex.common_mistakes) : ex.common_mistakes;
  return (
    <Dialog open={!!ex} onOpenChange={onClose}>
      <DialogContent className="max-w-lg gap-0 overflow-hidden p-0">
        <div key={ex.id} className="max-h-[85vh] overflow-y-auto no-scrollbar px-6 pt-6 pb-28">
          <DialogHeader className="space-y-3">
            <div className="flex items-start justify-between gap-3 pr-6">
              <DialogTitle className="font-display text-2xl leading-tight">{lang === "id" ? ex.name_id : ex.name}</DialogTitle>
              {onToggleFav && (
                <button onClick={onToggleFav} data-testid="detail-fav-btn"
                  className={cn("grid h-10 w-10 shrink-0 place-items-center rounded-xl border transition-colors",
                    isFav ? "border-amber-400/40 bg-amber-400/10 text-amber-400" : "text-muted-foreground hover:text-amber-400")}>
                  <Star className={cn("h-4 w-4", isFav && "fill-current")} />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge className="capitalize">{txCategory(ex.category, lang)}</Badge>
              <Badge variant="secondary" className="capitalize">{txEquipment(ex.equipment, lang)}</Badge>
              <Badge variant="outline" className="capitalize"><DifficultyText level={ex.difficulty} lang={lang} /></Badge>
            </div>
          </DialogHeader>

          <div className="mt-6 space-y-6 text-sm">
            <ExerciseGif url={ex.gif_url} name={id ? ex.name_id : ex.name} label={t("movementDemo")} className="mt-2 aspect-square w-full" />
            <Info label={t("muscleGroups")} value={txMuscles([...(ex.primary_muscles || []), ...(ex.secondary_muscles || [])], lang)} />
            {instructions && (
              <div>
                <SectionLabel>{t("instructions")}</SectionLabel>
                <p className="mt-2 rounded-xl bg-accent/50 p-4 leading-relaxed">{instructions}</p>
              </div>
            )}
            {formCues?.length > 0 && <TipList label={t("formCues")} items={formCues} variant="do" />}
            {commonMistakes?.length > 0 && <TipList label={t("commonMistakes")} items={commonMistakes} variant="dont" />}
            {onOpenExercise && <AlternativesList exId={ex.id} lang={lang} onSelect={onOpenExercise} />}
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 border-t bg-background/80 p-4 backdrop-blur-sm">
          <a href={ytSearchUrl(id ? (ex.name_id || ex.name) : ex.name, id)}
            target="_blank" rel="noopener noreferrer" data-testid="exercise-video-link" className="block">
            <Button className="w-full gap-2 bg-red-600 text-white hover:bg-red-700"><Youtube className="h-4 w-4" /> {t("watchVideo")}</Button>
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const SectionLabel = ({ children }) => (
  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</div>
);

const Info = ({ label, value }) => value ? (
  <div><SectionLabel>{label}</SectionLabel><p className="mt-1.5 font-medium">{value}</p></div>
) : null;

const TipList = ({ label, items, variant }) => {
  const isDo = variant === "do";
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <ul className="mt-2 space-y-2">
        {items.map((i, k) => (
          <li key={k} className="flex items-start gap-2.5 rounded-lg bg-accent/40 px-3 py-2 leading-relaxed">
            <span className={cn("mt-0.5 shrink-0", isDo ? "text-emerald-500" : "text-destructive")}>
              {isDo ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            </span>
            <span>{i}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

function CustomExerciseDialog({ open, onClose, onSaved }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", name_id: "", category: "strength", equipment: "bodyweight", difficulty: "beginner", type: "weight_reps", primary_muscles: [], instructions: "" });
  const toggleMuscle = (m) => setForm((f) => ({
    ...f,
    primary_muscles: f.primary_muscles.includes(m) ? f.primary_muscles.filter((x) => x !== m) : [...f.primary_muscles, m],
  }));
  const save = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/exercises", { ...form, name_id: form.name_id || form.name, primary_muscles: form.primary_muscles });
    toast.success(t("save")); onSaved(); onClose();
    setForm({ name: "", name_id: "", category: "strength", equipment: "bodyweight", difficulty: "beginner", type: "weight_reps", primary_muscles: [], instructions: "" });
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("customExercise")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t("name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="custom-ex-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("difficulty")}</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.slice(1).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>{t("equipment")}</Label>
              <Select value={form.equipment} onValueChange={(v) => setForm({ ...form, equipment: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["barbell", "dumbbell", "machine", "cable", "bodyweight", "kettlebell", "band", "other"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div>
            <Label>{t("muscleGroups")}</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="custom-ex-muscles">
              {MUSCLE_OPTIONS.map((m) => {
                const active = form.primary_muscles.includes(m);
                return (
                  <button type="button" key={m} onClick={() => toggleMuscle(m)} data-testid={`muscle-${m}`}
                    className={cn("rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                      active ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent")}>
                    {txMuscle(m, lang)}
                  </button>
                );
              })}
            </div>
          </div>
          <div><Label>{t("instructions")}</Label><Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-custom-ex">{t("save")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Session ---------------- */
function Session({ active, onChange }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [session, setSession] = useState(active || null);
  const [picker, setPicker] = useState(false);
  const [summary, setSummary] = useState(null);
  const [note, setNote] = useState(active?.notes || "");
  const [savingNote, setSavingNote] = useState(false);
  const [cooldownPrompt, setCooldownPrompt] = useState(false);
  const [cooldownView, setCooldownView] = useState(false);
  const promptedRef = useRef(false);

  const allSetsDone = useMemo(() => {
    const allSets = (session?.logs || []).flatMap((l) => l.sets || []);
    return allSets.length > 0 && allSets.every((s) => s.done);
  }, [session]);

  useEffect(() => {
    if (allSetsDone && !promptedRef.current) { promptedRef.current = true; setCooldownPrompt(true); }
    if (!allSetsDone) promptedRef.current = false;
  }, [allSetsDone]);

  useEffect(() => { if (!session) { setCooldownView(false); setCooldownPrompt(false); } }, [session]);

  useEffect(() => { setSession(active || null); setNote(active?.notes || ""); }, [active]);

  const start = async () => {
    const { data } = await api.post("/sessions", { name: t("freeWorkout") });
    setSession(data); onChange();
  };

  const persist = async (logs) => {
    setSession((s) => ({ ...s, logs }));
    await api.put(`/sessions/${session.id}`, { logs });
  };

  const addExercise = async (ex) => {
    const logs = [...(session.logs || []), { exercise_id: ex.id, exercise_name: lang === "id" ? ex.name_id : ex.name, type: ex.type, sets: [{ weight: "", reps: "", warmup: false }] }];
    setPicker(false); await persist(logs);
  };

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await api.put(`/sessions/${session.id}`, { notes: note });
      setSession((s) => ({ ...s, notes: note }));
      toast.success(t("noteSaved"));
    } catch { toast.error("Error"); } finally { setSavingNote(false); }
  };

  const end = async () => {
    const { data } = await api.post(`/sessions/${session.id}/end`, { logs: session.logs, notes: note });
    setSummary(data); setSession(null); onChange();
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["sessions"] });
    qc.invalidateQueries({ queryKey: ["beginner-program"] });
    if (data.prs?.length) toast.success(`${t("newPR")} ${data.prs.join(", ")}`);
  };

  const cancel = async () => {
    try { await api.delete(`/sessions/${session.id}`); } catch { /* ignore */ }
    setSession(null); onChange();
    qc.invalidateQueries({ queryKey: ["sessions"] });
    toast.success(t("sessionCancelled"));
  };

  if (summary) return <SessionSummary summary={summary} onClose={() => setSummary(null)} />;

  if (cooldownView && session) {
    return (
      <div className="slide-up space-y-4" data-testid="session-cooldown">
        <button onClick={() => setCooldownView(false)} data-testid="cooldown-back-to-session"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t("backToSession")}
        </button>
        <Cooldown embedded onEnd={end} />
      </div>
    );
  }

  if (!session) {
    return (
      <EmptyState icon={Play} title={t("startWorkout")} description={t("freeWorkout")}
        action={<Button onClick={start} size="lg" className="gap-2" data-testid="start-session-btn"><Play className="h-4 w-4" /> {t("startWorkout")}</Button>} />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><GymTermsGlossary /></div>
      {(() => {
        const allSets = (session.logs || []).flatMap((l) => l.sets || []);
        const total = allSets.length;
        const done = allSets.filter((s) => s.done).length;
        const pct = total ? Math.round((done / total) * 100) : 0;
        if (!total) return null;
        return (
          <div className="rounded-2xl border bg-card p-4" data-testid="session-progress">
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-display font-semibold">{t("sessionProgress")}</span>
              <span className="tabular-nums font-semibold text-primary" data-testid="session-progress-count">{done}/{total} {t("setsCompleted")}</span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-muted">
              <div className={cn("h-full rounded-full transition-all duration-500", pct === 100 ? "bg-emerald-500" : "bg-primary")} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })()}
      <div className="space-y-4">
        {(session.logs || []).map((log, li) => (
          <ExerciseLog key={li} log={log}
            onUpdate={(sets) => { const logs = [...session.logs]; logs[li] = { ...log, sets }; persist(logs); }}
            onReplace={(a) => {
              const logs = [...session.logs];
              logs[li] = { ...log, exercise_id: a.id, exercise_name: lang === "id" ? a.name_id || a.name : a.name, type: a.type,
                sets: (log.sets || []).map((s) => ({ weight: "", reps: s.reps ?? "", warmup: !!s.warmup, done: false })) };
              persist(logs);
              toast.success(lang === "id" ? "Gerakan diganti" : "Exercise swapped");
            }}
            onRemove={() => { const logs = session.logs.filter((_, i) => i !== li); persist(logs); }} />
        ))}
      </div>
      <Button variant="outline" onClick={() => setPicker(true)} className="w-full gap-2" data-testid="add-exercise-btn"><Plus className="h-4 w-4" /> {t("addExercise")}</Button>

      <div className="rounded-2xl border bg-card p-4" data-testid="session-note-card">
        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("sessionNote")}</Label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={280}
          placeholder={t("sessionNotePlaceholder")} className="mt-2 min-h-16 text-sm" data-testid="session-note-input" />
        <Button onClick={saveNote} disabled={savingNote || note === (session.notes || "")} variant="outline" size="sm" className="mt-2 gap-2" data-testid="save-note-btn">
          {savingNote ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Check className="h-4 w-4" />} {t("saveNote")}
        </Button>
      </div>
      <div className="sticky bottom-20 flex gap-2 lg:bottom-4">
        <Button onClick={end} disabled={!session.logs?.length} className="flex-1 gap-2" size="lg" data-testid="end-session-btn"><Check className="h-4 w-4" /> {t("endSession")}</Button>
        <Button onClick={cancel} variant="outline" size="lg" className="gap-2" data-testid="cancel-session-btn"><X className="h-4 w-4" /> {t("cancelSession")}</Button>
      </div>
      {!session.logs?.length && (
        <p className="text-center text-xs text-muted-foreground">{t("addExerciseToFinish")}</p>
      )}
      <ExercisePicker open={picker} onClose={() => setPicker(false)} onPick={addExercise} />

      <Dialog open={cooldownPrompt} onOpenChange={setCooldownPrompt}>
        <DialogContent className="max-w-sm" data-testid="cooldown-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-lg"><Snowflake className="h-5 w-5 text-cyan-500" /> {t("cooldownPromptTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("cooldownPromptDesc")}</p>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row-reverse">
            <Button onClick={() => { setCooldownPrompt(false); setCooldownView(true); }} className="flex-1 gap-2 bg-cyan-500 text-white hover:bg-cyan-600" data-testid="cooldown-prompt-start"><Snowflake className="h-4 w-4" /> {t("cooldownFirst")}</Button>
            <Button variant="outline" onClick={() => { setCooldownPrompt(false); end(); }} className="flex-1 gap-2" data-testid="cooldown-prompt-finish"><Check className="h-4 w-4" /> {t("finishNow")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExerciseLog({ log, onUpdate, onRemove, onReplace }) {
  const { t, lang } = useI18n();
  const [showSwap, setShowSwap] = useState(false);
  const isCardio = log.type === "time_distance";
  const sets = log.sets || [];
  const doneCount = sets.filter((s) => s.done).length;
  const allDone = sets.length > 0 && doneCount === sets.length;
  const cardRef = useRef(null);
  const prevAllDone = useRef(allDone);

  const { data: last } = useQuery({
    queryKey: ["last-set", log.exercise_id],
    queryFn: async () => (await api.get(`/exercises/${log.exercise_id}/last`)).data,
    enabled: !!log.exercise_id && !isCardio,
    staleTime: 60_000,
  });
  const hasLast = last?.weight && last?.reps;

  const [showTips, setShowTips] = useState(false);
  const { data: exDetail } = useQuery({
    queryKey: ["ex-detail", log.exercise_id],
    queryFn: async () => (await api.get(`/exercises/${log.exercise_id}`)).data,
    enabled: !!log.exercise_id && showTips,
    staleTime: 300_000,
  });

  useEffect(() => {
    if (allDone && !prevAllDone.current) {
      const rect = cardRef.current?.getBoundingClientRect();
      const origin = rect
        ? { x: (rect.left + rect.width / 2) / window.innerWidth, y: (rect.top + 40) / window.innerHeight }
        : { y: 0.6 };
      confetti({ particleCount: 70, spread: 65, startVelocity: 32, gravity: 1.1, ticks: 120, scalar: 0.9,
        origin, colors: ["#10b981", "#34d399", "#a7f3d0", "#059669", "#f59e0b"] });
    }
    prevAllDone.current = allDone;
  }, [allDone]);
  const addSet = () => onUpdate([...(log.sets || []), { weight: "", reps: "", warmup: false }]);
  const updSet = (i, k, v) => {
    const s = [...log.sets];
    const ns = { ...s[i], [k]: v };
    const filled = ns.weight !== "" && ns.weight != null && ns.reps !== "" && ns.reps != null;
    ns.done = filled;  // auto-complete once both weight & reps are filled
    s[i] = ns;
    onUpdate(s);
  };
  const rmSet = (i) => onUpdate(log.sets.filter((_, x) => x !== i));
  const toggleDone = (i) => {
    const s = [...log.sets];
    s[i] = { ...s[i], done: !s[i].done };
    onUpdate(s);
  };

  return (
    <div ref={cardRef} className={cn("rounded-2xl border bg-card p-4 transition-colors", allDone && "border-emerald-500/40")}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className={cn("flex items-center gap-2 font-display font-semibold", allDone && "text-emerald-600")}>
          {allDone && <Check className="h-4 w-4 shrink-0 text-emerald-500" />}{log.exercise_name}
        </h3>
        <div className="flex items-center gap-2">
          <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums", allDone ? "bg-emerald-500/15 text-emerald-600" : doneCount > 0 ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground")} data-testid="exercise-set-progress">
            {doneCount}/{sets.length}
          </span>
          {log.exercise_id && (
            <button onClick={() => setShowSwap(true)} title={lang === "id" ? "Ganti gerakan" : "Swap exercise"} data-testid="exercise-swap-btn"
              className="grid h-7 w-7 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-primary">
              <ArrowLeftRight className="h-4 w-4" />
            </button>
          )}
          {log.exercise_id && (
            <button onClick={() => setShowTips(true)} title={t("techniqueGuide")} data-testid="exercise-tips-btn"
              className="grid h-7 w-7 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-primary">
              <HelpCircle className="h-4 w-4" />
            </button>
          )}
          <button onClick={onRemove} className="text-muted-foreground hover:text-destructive" data-testid="remove-exercise"><X className="h-4 w-4" /></button>
        </div>
      </div>

      <Dialog open={showSwap} onOpenChange={setShowSwap}>
        <DialogContent className="max-w-md" data-testid="exercise-swap-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-lg"><ArrowLeftRight className="h-5 w-5 text-primary" /> {log.exercise_name}</DialogTitle>
          </DialogHeader>
          {showSwap && <AlternativesList exId={log.exercise_id} lang={lang} mode="swap" onSelect={(a) => { setShowSwap(false); onReplace?.(a); }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={showTips} onOpenChange={setShowTips}>
        <DialogContent className="max-w-md" data-testid="exercise-tips-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-lg"><HelpCircle className="h-5 w-5 text-primary" /> {log.exercise_name}</DialogTitle>
          </DialogHeader>
          {exDetail ? (
            <div className="space-y-5 text-sm">
              {(() => {
                const idLang = lang === "id";
                const cues = idLang ? (exDetail.form_cues_id?.length ? exDetail.form_cues_id : exDetail.form_cues) : exDetail.form_cues;
                const mistakes = idLang ? (exDetail.common_mistakes_id?.length ? exDetail.common_mistakes_id : exDetail.common_mistakes) : exDetail.common_mistakes;
                const instr = idLang ? (exDetail.instructions_id || exDetail.instructions) : exDetail.instructions;
                return (
                  <>
                    {exDetail.gif_url && <ExerciseGif url={exDetail.gif_url} name={log.exercise_name} className="aspect-square w-full" />}
                    {instr && <p className="rounded-xl bg-accent/50 p-3 leading-relaxed">{instr}</p>}
                    {cues?.length > 0 && <TipList label={t("formCues")} items={cues} variant="do" />}
                    {mistakes?.length > 0 && <TipList label={t("commonMistakes")} items={mistakes} variant="dont" />}
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          )}
        </DialogContent>
      </Dialog>

      {hasLast && (
        <div className="mb-3 flex items-center gap-1.5 rounded-lg bg-accent/50 px-3 py-1.5 text-xs text-muted-foreground" data-testid="last-set-hint">
          <Clock className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span>{t("lastTime")}: <span className="font-semibold tabular-nums text-foreground">{last.weight} kg × {last.reps} {t("reps").toLowerCase()}</span></span>
        </div>
      )}

      <div className="space-y-2">
        <div className="grid grid-cols-[2rem_1fr_1fr_2.25rem_2.25rem] gap-2 px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <span className="text-center">{t("set")}</span>
          <span>{isCardio ? "km" : `${t("weight")} (kg)`}</span>
          <span>{isCardio ? "min" : t("reps")}</span>
          <span></span><span></span>
        </div>
        {(log.sets || []).map((s, i) => (
          <div key={i} className={cn("grid grid-cols-[2rem_1fr_1fr_2.25rem_2.25rem] items-center gap-2 rounded-lg", s.done && "bg-primary/5")}>
            <button onClick={() => updSet(i, "warmup", !s.warmup)} data-testid={`set-warmup-${i}`}
              className={cn("grid h-9 w-9 place-items-center rounded-lg text-xs font-semibold tabular-nums", s.warmup ? "bg-warning/20 text-warning" : "bg-muted")}>
              {s.warmup ? "W" : i + 1}
            </button>
            <Input type="number" inputMode="decimal" step="0.5" value={s.weight} placeholder={hasLast ? String(last.weight) : (isCardio ? "km" : "kg")} onChange={(e) => updSet(i, "weight", parseFloat(e.target.value) || "")} className="h-10 min-w-0 px-1 text-center tabular-nums placeholder:text-muted-foreground/40 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" data-testid={`set-weight-${i}`} />
            <Input type="number" inputMode="numeric" value={s.reps} placeholder={hasLast ? String(last.reps) : (isCardio ? "min" : "rep")} onChange={(e) => updSet(i, "reps", parseFloat(e.target.value) || "")} className="h-10 min-w-0 px-1 text-center tabular-nums placeholder:text-muted-foreground/40 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" data-testid={`set-reps-${i}`} />
            <button onClick={() => toggleDone(i)} title={t("completeSet")} data-testid={`set-done-${i}`}
              className={cn("grid h-9 w-9 place-items-center rounded-lg transition-colors", s.done ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-primary")}>
              <Check className="h-4 w-4" />
            </button>
            <button onClick={() => rmSet(i)} className="grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <Button variant="ghost" size="sm" onClick={addSet} className="mt-2 gap-1 text-primary" data-testid="add-set-btn"><Plus className="h-3 w-3" /> {t("addSet")}</Button>
    </div>
  );
}

function ExercisePicker({ open, onClose, onPick }) {
  const { t, lang } = useI18n();
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const { data: exercises } = useQuery({ queryKey: ["picker-exercises", dq], queryFn: async () => (await api.get("/exercises", { params: { q: dq, limit: 60 } })).data, enabled: open });
  const { data: favorites } = useQuery({ queryKey: ["favorites"], queryFn: async () => (await api.get("/favorites")).data, enabled: open });
  const favSet = new Set(favorites || []);
  const { data: favExercises = [] } = useQuery({ queryKey: ["picker-fav-exercises", favorites], queryFn: async () => (await api.get("/exercises", { params: { favorites: true, limit: 200 } })).data, enabled: open });
  const showFavSection = !q && favExercises.length > 0;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{t("addExercise")}</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9" data-testid="picker-search" />
        </div>
        <div className="max-h-80 space-y-1 overflow-y-auto no-scrollbar">
          {showFavSection && (
            <>
              <div className="flex items-center gap-1.5 px-3 pt-1 text-xs font-semibold uppercase tracking-wide text-amber-500"><Star className="h-3.5 w-3.5 fill-current" /> {t("favorites")}</div>
              {favExercises.map((ex) => (
                <button key={`fav-${ex.id}`} onClick={() => onPick(ex)} data-testid={`pick-fav-${ex.id}`}
                  className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-accent">
                  <div className="flex items-center gap-2">
                    <Star className="h-3.5 w-3.5 text-amber-400 fill-current" />
                    <div><div className="text-sm font-medium">{lang === "id" ? ex.name_id : ex.name}</div>
                      <div className="text-xs text-muted-foreground capitalize">{txCategory(ex.category, lang)} · {txEquipment(ex.equipment, lang)}</div></div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
              <div className="my-1 border-t" />
            </>
          )}
          {(exercises || []).map((ex) => (
            <button key={ex.id} onClick={() => onPick(ex)} data-testid={`pick-${ex.id}`}
              className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-accent">
              <div><div className="text-sm font-medium">{lang === "id" ? ex.name_id : ex.name}</div>
                <div className="text-xs text-muted-foreground capitalize">{txCategory(ex.category, lang)} · {txEquipment(ex.equipment, lang)}</div></div>
              {favSet.has(ex.id) ? <Star className="h-4 w-4 text-amber-400 fill-current" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SessionSummary({ summary, onClose }) {
  const { t } = useI18n();
  const hasPR = summary.prs?.length > 0;
  const perEx = summary.per_exercise || [];
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);

  const shareToFeed = async () => {
    setSharing(true);
    try {
      const stats = `${summary.exercises} ${t("exercisesLabel").toLowerCase()} · ${summary.total_sets} ${t("set").toLowerCase()} · ${(summary.volume || 0).toLocaleString()} kg`;
      const prLine = hasPR ? `\n🏆 ${t("newPR")} ${summary.prs.join(", ")}` : "";
      const text = `${t("shareWorkoutText")}\n${stats}${prLine}`;
      await api.post("/posts", { text });
      setShared(true);
      toast.success(t("sharedToFeed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSharing(false); }
  };

  useEffect(() => {
    const shots = hasPR ? 3 : 2;
    const colors = ["#10b981", "#34d399", "#6ee7b7", "#fbbf24"];
    for (let i = 0; i < shots; i++) {
      setTimeout(() => confetti({
        particleCount: 70, spread: 75, startVelocity: 38, gravity: 1.05, ticks: 140,
        scalar: 0.95, origin: { y: 0.55 }, colors,
      }), i * 260);
    }
  }, [hasPR]);

  return (
    <div className="mx-auto max-w-md space-y-4 slide-up" data-testid="session-summary">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl border bg-gradient-to-b from-primary/15 via-card to-card p-8 text-center">
        <div className="pointer-events-none absolute -top-16 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full bg-primary/25 blur-3xl" />
        <div className="relative mx-auto grid h-20 w-20 place-items-center rounded-2xl bg-primary/20 text-primary ring-1 ring-primary/30">
          <Trophy className="h-10 w-10" />
        </div>
        <h2 className="relative mt-5 font-display text-3xl font-bold">{t("summaryTitle")}</h2>
        <p className="relative mt-1.5 text-sm text-muted-foreground">{t("summarySubtitle")}</p>
        {summary.streak > 0 && (
          <div className="relative mx-auto mt-4 inline-flex items-center gap-1.5 rounded-full bg-orange-500/15 px-3.5 py-1.5 text-sm font-semibold text-orange-500" data-testid="summary-streak">
            <Flame className="h-4 w-4" /> {summary.streak} {t("streakDays")}
          </div>
        )}
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 gap-3">
        <Stat icon={Clock} value={summary.duration_min} unit=" min" label={t("duration")} />
        <Stat icon={Dumbbell} value={(summary.volume || 0).toLocaleString()} unit=" kg" label={t("volume")} />
        <Stat icon={Flame} value={summary.total_sets} unit="" label={t("set")} />
        <Stat icon={Repeat} value={summary.total_reps} unit="" label={t("totalReps")} />
      </div>

      {/* PR banner */}
      {hasPR && (
        <div className="rounded-2xl border border-primary/30 bg-primary/10 p-4" data-testid="summary-pr">
          <div className="flex items-center gap-2 font-display font-bold text-primary">
            <Award className="h-5 w-5" /> {t("newPRTitle")}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {summary.prs.map((p, i) => (
              <span key={i} className="rounded-full bg-primary/15 px-2.5 py-1 text-xs font-semibold text-primary">{p}</span>
            ))}
          </div>
        </div>
      )}

      {/* Breakdown */}
      {perEx.length > 0 && (
        <div className="rounded-2xl border bg-card p-4" data-testid="summary-breakdown">
          <div className="mb-3 flex items-center gap-2 text-sm font-display font-semibold">
            <TrendingUp className="h-4 w-4 text-primary" /> {t("breakdownTitle")}
          </div>
          <div className="space-y-2.5">
            {perEx.map((ex, i) => (
              <div key={i} className="flex items-center justify-between gap-3 rounded-xl bg-muted/40 px-3 py-2.5" data-testid={`summary-ex-${i}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-medium">{ex.name}</span>
                    {ex.is_pr && <Award className="h-3.5 w-3.5 shrink-0 text-primary" />}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {ex.sets} {t("set").toLowerCase()} · {ex.reps} {t("reps").toLowerCase()}
                    {ex.best_set && ` · ${t("bestSetLabel")} ${ex.best_set.weight}kg×${ex.best_set.reps}`}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="font-display text-sm font-bold tabular-nums">{(ex.volume || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground">kg</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button onClick={shareToFeed} disabled={sharing || shared} size="lg" variant="outline" className="flex-1 gap-2" data-testid="summary-share-btn">
          {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : shared ? <Check className="h-4 w-4 text-primary" /> : <Share2 className="h-4 w-4" />}
          {shared ? t("sharedToFeed") : t("shareToFeed")}
        </Button>
        <Button onClick={onClose} size="lg" className="flex-1 gap-2" data-testid="summary-close">
          <Check className="h-4 w-4" /> {t("summaryDone")}
        </Button>
      </div>
    </div>
  );
}
const Stat = ({ icon: Icon, value, unit, label }) => (
  <div className="rounded-2xl border bg-card p-4">
    <Icon className="h-4 w-4 text-primary" />
    <div className="mt-2 font-display text-2xl font-bold tabular-nums">{value ?? 0}<span className="text-sm font-normal text-muted-foreground">{unit}</span></div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);

/* ---------------- History ---------------- */
function History({ onStarted }) {
  const { t, lang } = useI18n();
  const [selected, setSelected] = useState(null);
  const { data: sessions, isLoading } = useQuery({ queryKey: ["sessions"], queryFn: async () => (await api.get("/sessions")).data });
  if (isLoading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>;
  const completed = (sessions || []).filter((s) => s.status === "completed");
  if (!completed.length) return <EmptyState icon={Dumbbell} title={t("emptyExercises")} description={t("startTracking")} />;
  return (
    <div className="space-y-3">
      {completed.map((s) => (
        <button key={s.id} onClick={() => setSelected(s)} data-testid={`session-${s.id}`}
          className="flex w-full items-center justify-between rounded-2xl border bg-card p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40">
          <div className="min-w-0">
            <div className="truncate font-display font-semibold">{s.name}</div>
            <div className="text-xs text-muted-foreground">{formatDate(s.date, lang)} · {(s.logs || []).length} {t("exerciseLibrary").toLowerCase()}</div>
          </div>
          <div className="flex items-center gap-4 text-right text-sm">
            <div><div className="font-display font-bold tabular-nums">{s.summary?.duration_min || 0}</div><div className="text-[11px] text-muted-foreground">min</div></div>
            <div><div className="font-display font-bold tabular-nums">{(s.summary?.volume || 0).toLocaleString()}</div><div className="text-[11px] text-muted-foreground">kg</div></div>
            {s.summary?.prs?.length > 0 && <Trophy className="h-4 w-4 text-primary" />}
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </button>
      ))}
      <HistoryDetail session={selected} onClose={() => setSelected(null)} onStarted={onStarted} />
    </div>
  );
}

function HistoryDetail({ session, onClose, onStarted }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [repeating, setRepeating] = useState(false);
  if (!session) return null;
  const logs = session.logs || [];
  const sm = session.summary || {};

  const repeat = async () => {
    setRepeating(true);
    try {
      const newLogs = logs.map((lg) => ({
        exercise_id: lg.exercise_id,
        exercise_name: lg.exercise_name,
        type: lg.type,
        sets: (lg.sets || [{}]).map(() => ({ weight: "", reps: "", warmup: false })),
      }));
      const { data: ns } = await api.post("/sessions", { name: session.name });
      await api.put(`/sessions/${ns.id}`, { logs: newLogs });
      await qc.invalidateQueries({ queryKey: ["active-session"] });
      onStarted?.();
      onClose?.();
      toast.success(t("workoutRepeated"));
    } catch { toast.error("Error"); } finally { setRepeating(false); }
  };

  return (
    <Dialog open={!!session} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{session.name}</DialogTitle>
          <p className="text-sm text-muted-foreground">{formatDate(session.date, lang, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <Stat icon={Clock} value={sm.duration_min || 0} unit="min" label={t("duration")} />
          <Stat icon={Dumbbell} value={(sm.volume || 0).toLocaleString()} unit="kg" label={t("volume")} />
          <Stat icon={Flame} value={sm.total_sets || 0} unit="" label={t("set")} />
        </div>
        {sm.prs?.length > 0 && (
          <div className="rounded-xl bg-primary/10 p-3 text-sm font-medium text-primary">🏆 {t("newPR")} {sm.prs.join(", ")}</div>
        )}
        {session.notes && (
          <div className="rounded-xl border bg-card p-3" data-testid="history-note">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("sessionNote")}</div>
            <p className="mt-1 text-sm">{session.notes}</p>
          </div>
        )}
        <div className="space-y-3">
          {logs.map((lg, i) => {
            const sets = lg.sets || [];
            return (
              <div key={i} className="rounded-2xl border bg-card p-4" data-testid={`history-exercise-${i}`}>
                <div className="flex items-center gap-2.5">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-xs font-bold text-primary tabular-nums">{i + 1}</span>
                  <span className="flex-1 truncate font-display font-semibold">{lg.exercise_name}</span>
                  <a href={ytSearchUrl(lg.exercise_name, true)} target="_blank" rel="noopener noreferrer" aria-label={t("videoGuide")}
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-red-500 transition-colors hover:bg-red-500/10">
                    <Youtube className="h-4 w-4" />
                  </a>
                </div>
                {sets.length ? (
                  <div className="mt-3 space-y-1.5">
                    {sets.map((st, k) => (
                      <div key={k} className="flex items-center gap-3 text-sm">
                        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-accent text-[11px] font-bold tabular-nums text-muted-foreground">{k + 1}</span>
                        <span className="tabular-nums">{st.weight || 0} kg × {st.reps || 0} {t("reps").toLowerCase()}</span>
                        {st.warmup && <Badge variant="outline" className="text-[10px]">{t("warmup")}</Badge>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">{t("noSetsLogged")}</p>
                )}
              </div>
            );
          })}
        </div>
        {logs.length > 0 && (
          <Button onClick={repeat} disabled={repeating} size="lg" className="w-full gap-2" data-testid="repeat-workout-btn">
            {repeating ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Play className="h-4 w-4" />} {t("repeatWorkout")}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
